import { createListenerMiddleware } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react-native'
import { upperFirst } from 'lodash'
import {
  TSRestEndpoints,
  TSRestApiRejectedAction,
  TSRestApiPendingAction,
  TSRestApiFulfilledAction,
} from '../api'
import { isErrorsAbortedDueToFalseCondition } from '@/src/clients/sentry/store/listeners'
import { isRTKQueryAction } from '@/src/clients/sentry/store/listeners/captureQueryFnErrors'
import { sentryCaptureException } from '@/src/common/hooks/useLogError'
import { ErrorLike } from '@/src/clients/sentry/store/listeners/captureQueryFnErrors'

export const sentryTransactions = createListenerMiddleware()

const activeSpans = new Map<
  string,
  { span: ReturnType<typeof Sentry.getActiveSpan>; startTime: number }
>()

const getSpanKey = (
  action:
    | TSRestApiPendingAction<keyof TSRestEndpoints>
    | TSRestApiFulfilledAction<keyof TSRestEndpoints>
    | TSRestApiRejectedAction<keyof TSRestEndpoints>,
): string => {
  const { endpointName, requestId } = action.meta.arg
  return `${endpointName}-${requestId}`
}

const getSpanMeta = (
  action:
    | TSRestApiPendingAction<keyof TSRestEndpoints>
    | TSRestApiFulfilledAction<keyof TSRestEndpoints>
    | TSRestApiRejectedAction<keyof TSRestEndpoints>,
) => {
  const { endpointName, type } = action.meta.arg
  const QueryOrMutation = upperFirst(type)

  return {
    name: `RTKQ ${QueryOrMutation}: ${endpointName}`,
    op: 'rtkq.request',
    attributes: {
      endpoint: endpointName,
      type: QueryOrMutation,
    },
  }
}

sentryTransactions.startListening({
  predicate: (action) => isRTKQueryAction(action) && action.type.endsWith('/pending'),
  effect: (action) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const pendingAction = action as TSRestApiPendingAction<keyof TSRestEndpoints>

    const spanKey = getSpanKey(pendingAction)
    const { name, op, attributes } = getSpanMeta(pendingAction)
    const startTime = Date.now()

    Sentry.startSpan(
      {
        name,
        op,
        attributes: {
          ...attributes,
          requestId: pendingAction.meta.requestId,
          endpointName: pendingAction.meta.arg.endpointName,
          startTime,
        },
      },
      (span) => {
        activeSpans.set(spanKey, { span, startTime })
        return span
      },
    )
  },
})

sentryTransactions.startListening({
  predicate: (action) => isRTKQueryAction(action) && action.type.endsWith('/fulfilled'),
  effect: (action) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const fulfilledAction = action as TSRestApiFulfilledAction<keyof TSRestEndpoints>

    const spanKey = getSpanKey(fulfilledAction)
    const fulfilledSpan = activeSpans.get(spanKey)

    if (fulfilledSpan) {
      const { span, startTime } = fulfilledSpan
      if (span) {
        span.setStatus({ code: 1, message: 'ok' })

        span.setAttributes({
          status: 'success',
          'rtkq.status': 'fulfilled',
          'rtkq.responseTime': Date.now() - startTime,
        })

        span.end()
      }

      activeSpans.delete(spanKey)
    }
  },
})

sentryTransactions.startListening({
  predicate: (action) => isRTKQueryAction(action) && action.type.endsWith('/rejected'),
  effect: (action) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const rejectedAction = action as TSRestApiRejectedAction<keyof TSRestEndpoints>

    const spanKey = getSpanKey(rejectedAction)
    const rejectedSpan = activeSpans.get(spanKey)
    const error = rejectedAction.error

    if (rejectedSpan) {
      const { span, startTime } = rejectedSpan

      if (span) {
        const { meta, type } = rejectedAction

        const title = `RTKQ Span Error: ${type}`

        span.setStatus({ code: 2, message: title })

        if (error) {
          const error = rejectedAction.error
          span.setAttributes({
            status: 'error',
            'rtkq.status': 'rejected',
            'rtkq.responseTime': Date.now() - startTime,
          })

          if (!isErrorsAbortedDueToFalseCondition(error)) {
            sentryCaptureException(
              ErrorLike.safeParse(meta?.baseQueryMeta).success ? meta?.baseQueryMeta : error,
              {
                title,
                tags: {
                  endpoint: meta?.arg?.endpointName,
                  requestId: meta?.requestId,
                },
                type,
                meta,
                error,
              },
            )
          }
        }

        span.end()
      }

      activeSpans.delete(spanKey)
    }
  },
})
